import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import AdminNav from './AdminNav';
import { FaSearch, FaUser, FaEnvelope, FaCalendar, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const UserAccountsManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = users.filter(user => 
        (user.email && user.email.toLowerCase().includes(query)) ||
        (user.displayName && user.displayName.toLowerCase().includes(query)) ||
        (user.uid && user.uid.toLowerCase().includes(query))
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Fetch all users from the 'users' collection in Firestore
      const usersCollection = collection(db, 'users');
      const usersQuery = query(usersCollection, orderBy('createdAt', 'desc'));
      const usersSnapshot = await getDocs(usersQuery);
      
      const usersList = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          uid: doc.id,
          email: data.email || '',
          displayName: data.displayName || '',
          photoURL: data.photoURL || '',
          createdAt: data.createdAt || null,
          emailVerified: data.emailVerified || false,
          role: data.role || 'customer',
          provider: data.provider || 'email'
        };
      });
      
      console.log('Fetched users:', usersList.length);
      setUsers(usersList);
      setFilteredUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Error loading users. Please check the console for details.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <Container>
      <AdminNav />
      
      <Header>
        <Title>User Accounts</Title>
        <Subtitle>View all registered user accounts</Subtitle>
      </Header>

      <SearchContainer>
        <SearchIcon>
          <FaSearch />
        </SearchIcon>
        <SearchInput
          type="text"
          placeholder="Search by email, name, or user ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchContainer>

      {loading ? (
        <LoadingMessage>Loading users...</LoadingMessage>
      ) : (
        <>
          <StatsBar>
            <StatItem>
              <StatLabel>Total Accounts:</StatLabel>
              <StatValue>{users.length}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Showing:</StatLabel>
              <StatValue>{filteredUsers.length}</StatValue>
            </StatItem>
          </StatsBar>

          {filteredUsers.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>User</TableHeader>
                    <TableHeader>Email</TableHeader>
                    <TableHeader>User ID</TableHeader>
                    <TableHeader>Created</TableHeader>
                    <TableHeader>Email Verified</TableHeader>
                    <TableHeader>Role</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {filteredUsers.map(user => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <UserInfo>
                          <UserAvatar>
                            {user.photoURL ? (
                              <AvatarImage src={user.photoURL} alt={user.displayName || 'User'} />
                            ) : (
                              <FaUser />
                            )}
                          </UserAvatar>
                          <UserName>{user.displayName || 'No Name'}</UserName>
                        </UserInfo>
                      </TableCell>
                      <TableCell>
                        <EmailContainer>
                          <FaEnvelope style={{ marginRight: '0.5rem', color: '#666' }} />
                          {user.email || 'No Email'}
                        </EmailContainer>
                      </TableCell>
                      <TableCell>
                        <UserIdText>{user.uid}</UserIdText>
                      </TableCell>
                      <TableCell>
                        <DateContainer>
                          <FaCalendar style={{ marginRight: '0.5rem', color: '#666' }} />
                          {formatDate(user.createdAt)}
                        </DateContainer>
                      </TableCell>
                      <TableCell>
                        <VerifiedBadge verified={user.emailVerified}>
                          {user.emailVerified ? (
                            <>
                              <FaCheckCircle /> Verified
                            </>
                          ) : (
                            <>
                              <FaTimesCircle /> Not Verified
                            </>
                          )}
                        </VerifiedBadge>
                      </TableCell>
                      <TableCell>
                        <RoleBadge isAdmin={user.role === 'admin'}>
                          {user.role === 'admin' ? 'Admin' : 'Customer'}
                        </RoleBadge>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          ) : (
            <EmptyState>
              <EmptyIcon><FaUser /></EmptyIcon>
              <EmptyTitle>No users found</EmptyTitle>
              <EmptyText>
                {searchQuery ? 'Try adjusting your search criteria' : 'No user accounts have been created yet'}
              </EmptyText>
            </EmptyState>
          )}
        </>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  max-width: 1400px;
  margin: 2rem auto;
  padding: 1.5rem;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const StatsBar = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f5f5f5;
  border-radius: 8px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatLabel = styled.span`
  color: #666;
  font-weight: 500;
`;

const StatValue = styled.span`
  color: #C41E3A;
  font-weight: 700;
  font-size: 1.1rem;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const TableHead = styled.thead`
  background-color: #f5f5f5;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f9f9f9;
  }
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #555;
  white-space: nowrap;
`;

const TableCell = styled.td`
  padding: 1rem;
  color: #333;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UserName = styled.div`
  font-weight: 500;
  color: #333;
`;

const EmailContainer = styled.div`
  display: flex;
  align-items: center;
  color: #666;
`;

const UserIdText = styled.code`
  font-size: 0.85rem;
  color: #666;
  background-color: #f5f5f5;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
`;

const DateContainer = styled.div`
  display: flex;
  align-items: center;
  color: #666;
  font-size: 0.9rem;
`;

const VerifiedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  background-color: ${props => props.verified ? '#d1f2eb' : '#f8d7da'};
  color: ${props => props.verified ? '#0f5132' : '#842029'};
  
  svg {
    font-size: 0.9rem;
  }
`;

const RoleBadge = styled.div`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  background-color: ${props => props.isAdmin ? '#cfe2ff' : '#e7e7e7'};
  color: ${props => props.isAdmin ? '#084298' : '#555'};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background-color: #f9f9f9;
  border-radius: 8px;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  color: #ddd;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  color: #666;
  margin-bottom: 0.5rem;
`;

const EmptyText = styled.p`
  color: #999;
`;

export default UserAccountsManagement;
